import React, { Component } from 'react';
import { toast } from 'react-toastify';
import { SessionContext } from './SessionContext.js';
import MessageList from './MessageList.js';
import MessageViewer from './MessageViewer.js';

class SimpleMailViewer extends Component {
    static contextType = SessionContext;

    constructor() {
        super();
        this.state = {
            messages: [],
            total: 0,
            unread: 0,
            selectedMessage: null,
            loading: false,
            loadingMore: false,
            loadingMessage: false,
            error: null,
            hasTriedToLoad: false
        };
    }

    async componentDidMount() {
        // Always try to load messages - loadMessages will handle the auth checks
        await this.loadMessages();
    }

    async componentDidUpdate(prevProps, prevState) {
        const { isReady, isLoggedIn } = this.context;
        
        // If context just became ready and we haven't loaded messages yet
        if (isReady && isLoggedIn && !this.state.hasTriedToLoad && this.state.messages.length === 0 && !this.state.loading) {
            await this.loadMessages();
        }
    }

    loadMessages = async () => {
        try {
            this.setState({ loading: true, error: null, hasTriedToLoad: true });
            const { isLoggedIn, isReady, accessToken } = this.context;
            
            if (!isReady) {
                this.setState({ loading: false, hasTriedToLoad: false });
                return;
            }
            
            if (!isLoggedIn) {
                this.setState({ error: 'Not logged in', loading: false });
                return;
            }

            if (!accessToken) {
                this.setState({ error: 'No access token', loading: false });
                return;
            }

            // Get API instance from context - we need to access it through the parent App component
            // For now, we'll create a new instance using the access token from context
            const NslMailAPI = (await import('../NslMailAPI.js')).default;
            const api = new NslMailAPI(this.context.accessToken);
            
            const page = await api.getMessages();
            this.setState({
                messages: page.messages,
                total: page.total,
                unread: page.unread,
                loading: false
            });
        } catch (error) {
            console.error('Error loading messages:', error);
            this.setState({ error: error.message, loading: false });
        }
    }

    // Older messages are fetched a page at a time rather than all at once: the
    // largest inboxes here hold tens of thousands, and the cost of a page must
    // not depend on how many are behind it.
    loadOlderMessages = async () => {
        if (this.state.loadingMore) {
            return;
        }

        try {
            this.setState({ loadingMore: true });

            const NslMailAPI = (await import('../NslMailAPI.js')).default;
            const api = new NslMailAPI(this.context.accessToken);

            const page = await api.getMessages(this.state.messages.length);
            this.setState(prevState => ({
                messages: prevState.messages.concat(page.messages),
                total: page.total,
                unread: page.unread,
                loadingMore: false
            }));
        } catch (error) {
            console.error('Error loading older messages:', error);
            toast.error('Error loading older messages: ' + error.message);
            this.setState({ loadingMore: false });
        }
    }

    selectMessage = async (message) => {
        try {
            this.setState({ loadingMessage: true });
            
            const NslMailAPI = (await import('../NslMailAPI.js')).default;
            const api = new NslMailAPI(this.context.accessToken);
            
            const fullMessage = await api.getMessage(message.id);
            this.setState({ selectedMessage: fullMessage, loadingMessage: false });
            
            // Update the message as read in the list
            this.setState(prevState => ({
                messages: prevState.messages.map(msg => 
                    msg.id === message.id ? { ...msg, read: true } : msg
                )
            }));
        } catch (error) {
            console.error('Error loading message:', error);
            this.setState({ error: error.message, loadingMessage: false });
        }
    }

    deleteMessage = async (messageId) => {
        if (!window.confirm('Are you sure you want to delete this message?')) {
            return;
        }

        try {
            const NslMailAPI = (await import('../NslMailAPI.js')).default;
            const api = new NslMailAPI(this.context.accessToken);
            
            await api.deleteMessage(messageId);
            
            // Remove from messages list
            this.setState(prevState => ({
                messages: prevState.messages.filter(msg => msg.id !== messageId),
                total: Math.max(prevState.total - 1, 0),
                selectedMessage: prevState.selectedMessage?.id === messageId ? null : prevState.selectedMessage
            }));
            
            // Reload message count
            if (this.context.actions.loadMessageCount) {
                await this.context.actions.loadMessageCount();
            }
            
            toast.success('Message deleted successfully');
            
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Error deleting message: ' + error.message);
        }
    }

    sendReply = async (originalMessageId, subject, body) => {
        try {
            const NslMailAPI = (await import('../NslMailAPI.js')).default;
            const api = new NslMailAPI(this.context.accessToken);
            
            await api.sendReply(originalMessageId, subject, body);
            toast.success('Reply sent successfully!');
            
        } catch (error) {
            console.error('Error sending reply:', error);
            toast.error('Error sending reply: ' + error.message);
        }
    }

    render() {
        const { loading, loadingMore, loadingMessage, error, messages, total, unread, selectedMessage } = this.state;
        const { isReady, isLoggedIn } = this.context;

        // Show loading while app is initializing
        if (!isReady) {
            return (
                <div className="mail-viewer">
                    <div className="loading">Initializing...</div>
                </div>
            );
        }

        // Show login prompt if not logged in
        if (!isLoggedIn) {
            return (
                <div className="mail-viewer">
                    <div className="error">Please log in to view your mail.</div>
                </div>
            );
        }

        // Show loading while fetching messages
        if (loading) {
            return (
                <div className="mail-viewer">
                    <div className="loading">Loading messages...</div>
                </div>
            );
        }

        // Show error with retry option
        if (error) {
            return (
                <div className="mail-viewer">
                    <div className="error">Error: {error}</div>
                    <button onClick={this.loadMessages} className="btn btn-primary">
                        Retry
                    </button>
                </div>
            );
        }

        return (
            <div className="mail-viewer">
                <div className="mail-viewer-content">
                    <div className="mail-list-pane">
                        <MessageList 
                            messages={messages}
                            total={total}
                            unread={unread}
                            loadingMore={loadingMore}
                            selectedMessage={selectedMessage}
                            onSelectMessage={this.selectMessage}
                            onDeleteMessage={this.deleteMessage}
                            onRefresh={this.loadMessages}
                            onLoadOlder={this.loadOlderMessages}
                        />
                    </div>
                    
                    <div className="mail-content-pane">
                        {loadingMessage ? (
                            <div className="loading-message">
                                <p>Loading message...</p>
                            </div>
                        ) : selectedMessage ? (
                            <MessageViewer 
                                message={selectedMessage}
                                onDelete={() => this.deleteMessage(selectedMessage.id)}
                                onReply={this.sendReply}
                            />
                        ) : (
                            <div className="no-message-selected">
                                <p>Select a message to read</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default SimpleMailViewer;